import { defineStore, acceptHMRUpdate } from 'pinia'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAccountStore } from 'src/stores/account'
import { useTicketQuery } from 'src/composables/ticket/query/useTicketQuery'

import { ROUTES } from 'src/router/const'
import { TICKET_TABS, TICKET_STATUS } from 'src/services/consts/ticket.const'
import { TICKET_EDIT_STEPS } from 'src/services/consts/ticket-creation.const'
import { getCombinedTicketStatus, buildQuestionAnswerMap } from 'src/services/helpers/ticket.helper'

export const useTicketStore = defineStore('ticket', () => {
  const route = useRoute()
  const router = useRouter()
  const accountStore = useAccountStore()

  const ticketId = computed(() => route.query.ticketId)

  const { ticket, isTicketLoading, isTicketRefetching, ticketError, refetchTicket } =
    useTicketQuery(ticketId)

  const activeTab = computed(() => {
    const tabFromQuery = route.query.tab
    const tabExists = Object.values(TICKET_TABS).find((tab) => tab.name === tabFromQuery)
    return tabExists ? tabFromQuery : TICKET_TABS.information.name
  })
  const combinedStatus = computed(() => {
    if (!ticket.value) {
      return { color: 'accent', icon: 'file-text', label: '' }
    }
    return getCombinedTicketStatus(ticket.value)
  })
  const isApprovedOrDenied = computed(() => {
    if (!ticket.value) return false
    return ticket.value.approved === true || ticket.value.approved === false
  })
  const isDraft = computed(() => {
    return ticket.value?.ticketStatus === TICKET_STATUS.OPENED
  })
  const isPreprocessing = computed(() => {
    return ticket.value?.ticketStatus === TICKET_STATUS.PREPROCESS
  })

  const ticketQuestions = computed(() => {
    if (!ticket.value) return []
    if (isDraft.value || isPreprocessing.value) {
      return Object.values(
        buildQuestionAnswerMap(accountStore.newTicketQuestions, ticket.value.answers)
      )
    }
    return ticket.value.questionsAnswers ?? []
  })

  const filesById = computed(() => {
    const map = {}
    for (const file of ticket.value?.uploadedFiles || []) {
      map[file.fileId] = file
    }
    return map
  })

  function fileById(fileId) {
    return filesById.value[fileId] || null
  }

  function closeDrawer() {
    const newQuery = { ...route.query }
    delete newQuery.ticketId
    delete newQuery.tab
    router.replace({ query: newQuery })
  }

  function editDraft(queryParams = {}) {
    if (!ticket.value) return
    router.push({
      name: ROUTES.TICKET_EDIT,
      params: {
        ticketStep: TICKET_EDIT_STEPS.REQUEST_DETAILS,
        ticketId: ticket.value.ticketId,
      },
      query: queryParams,
    })
  }

  return {
    // State
    ticketId,
    isDrawerOpen: computed(() => !!ticketId.value),

    // Data
    ticket,
    isTicketLoading,
    isTicketRefetching,
    ticketError,

    // Computed
    combinedStatus,
    isApprovedOrDenied,
    isDraft,
    isPreprocessing,
    ticketQuestions,
    filesById,
    fileById,

    // Tabs
    activeTab,

    // Actions
    closeDrawer,
    editDraft,
    refetchTicket,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTicketStore, import.meta.hot))
}
